/**
 * FallenEdgePanel — Loser Intelligence dashboard surface.
 *
 * Backend: /api/v1/fallen-edge/* (7 advisory-only routes, envelope `{ok,data}`).
 * The engine guarantees `advisory_only: true` / `no_order_execution: true` /
 * `no_live_trading: true` on every row. The panel re-asserts this in a visible
 * badge and shows beginner-friendly explanations, never raw JSON.
 *
 * Lanes shown (per operator brief):
 *   - Top Losers Worth Watching            (latest)
 *   - Panic Reversal Candidates            (reversals)
 *   - Short Continuation Candidates        (short-continuations)
 *   - Losers to Avoid                      (avoid)
 *
 * Scores rendered: reversal, short continuation, avoid (0–100).
 * Missing inputs are surfaced via DataQualityChip — never fabricated.
 *
 * Marker strings: "fallen-edge", "phase-fallen-edge-ui-v1".
 */
import React, { useEffect, useState } from "react";
import SymbolChartButton from './SymbolChartButton';  // ADV-P0K
import {
  AdvisoryOnlyBadge,
  DataQualityChip,
  ScoreBar,
  ScoreChip,
  formatGeneratedAtET,
  formatLossPct,
  humanize,
} from "./ScoreBar";
import {
  getFallenEdgeLatest,
  getFallenEdgeReversals,
  getFallenEdgeShortContinuations,
  getFallenEdgeAvoid,
  type FallenEdgeCandidate,
  type FallenEdgePayload,
} from "../lib/fallenEdgeClient";

type LaneKey = "latest" | "reversals" | "shorts" | "avoid";

interface LaneDef {
  key: LaneKey;
  label: string;
  description: string;
  testid: string;
  emptyHint: string;
}

const LANES: LaneDef[] = [
  {
    key: "latest",
    label: "Top Losers Worth Watching",
    description:
      "Today's biggest losers ranked by whether the move looks tradable in either direction. Use this as a watchlist, not a buy/sell instruction.",
    testid: "fallen-edge-lane-latest",
    emptyHint: "No losers met the watchlist threshold on the latest engine run.",
  },
  {
    key: "reversals",
    label: "Panic Reversal Candidates",
    description:
      "Beaten-down names where the engine sees a possible reclaim setup. Wait for the entry trigger; respect the invalidation level.",
    testid: "fallen-edge-lane-reversals",
    emptyHint: "No reversal-quality losers identified on the latest run.",
  },
  {
    key: "shorts",
    label: "Short Continuation Candidates",
    description:
      "Losers where weakness looks more likely to extend. Listed for awareness — Adversyn never auto-shorts; live execution is gated.",
    testid: "fallen-edge-lane-shorts",
    emptyHint: "No short-continuation losers identified on the latest run.",
  },
  {
    key: "avoid",
    label: "Losers to Avoid",
    description:
      "High-uncertainty losers where neither direction has conviction. Stand aside until clearer data arrives.",
    testid: "fallen-edge-lane-avoid",
    emptyHint: "No avoid-tier losers flagged on the latest run.",
  },
];

interface LaneState {
  loading: boolean;
  payload: FallenEdgePayload | null;
  error: string | null;
}

const EMPTY_LANE: LaneState = { loading: true, payload: null, error: null };

function classifyLoserType(token: string | null | undefined): string {
  // Beginner-friendly map for engine-internal `loser_type` enum.
  const map: Record<string, string> = {
    fake_loser: "Likely overdone selloff",
    real_loser: "Real damage — weakness may extend",
    sympathy_loser: "Selling on related-name news",
    structural_breakdown: "Structural breakdown",
  };
  if (!token) return "(unavailable)";
  return map[token] || humanize(token);
}

function CandidateCard({
  c,
  showReversal,
  showShort,
  showAvoid,
}: {
  c: FallenEdgeCandidate;
  showReversal: boolean;
  showShort: boolean;
  showAvoid: boolean;
}) {
  const ticker = c.ticker || "(unknown)";
  const sector = c.sector || null;
  return (
    <li
      data-testid="fallen-edge-candidate-card"
      className="rounded-lg border border-slate-700 bg-slate-900/60 p-3 space-y-2"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-base font-black tracking-tight text-white">{ticker}</span>
            <SymbolChartButton symbol={c.ticker} />
            {c.company_name ? (
              <span className="text-xs text-slate-400 truncate">{c.company_name}</span>
            ) : null}
          </div>
          <div className="text-[11px] text-slate-400 mt-0.5">
            {classifyLoserType(c.loser_type)}
            {sector ? <span className="text-slate-500"> · {sector}</span> : null}
          </div>
        </div>
        <div className="text-right">
          <div className="text-sm font-bold text-rose-300">{formatLossPct(c.current_loss_pct)}</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Session</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {showReversal ? (
          <ScoreChip label="Reversal" value={c.reversal_score} tone="bullish" />
        ) : null}
        {showShort ? (
          <ScoreChip label="Short Cont." value={c.short_continuation_score} tone="bearish" />
        ) : null}
        {showAvoid ? (
          <ScoreChip label="Avoid" value={c.avoid_score} tone="warning" />
        ) : null}
        <DataQualityChip quality={c.data_quality} missing={c.missing_inputs} />
      </div>

      {c.beginner_explanation ? (
        <p className="text-xs text-slate-300 leading-snug">{c.beginner_explanation}</p>
      ) : (
        <p className="text-xs text-slate-500 italic">No beginner explanation available.</p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px]">
        <div className="rounded border border-slate-800 px-2 py-1.5">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Suggested entry trigger</div>
          <div className="text-slate-200">{humanize(c.entry_trigger)}</div>
        </div>
        <div className="rounded border border-slate-800 px-2 py-1.5">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Invalidation</div>
          <div className="text-slate-200">{humanize(c.invalidation_level)}</div>
        </div>
      </div>
    </li>
  );
}

function LaneSection({
  lane,
  state,
}: {
  lane: LaneDef;
  state: LaneState;
}) {
  const rows = state.payload?.candidates || [];
  const showReversal = lane.key === "latest" || lane.key === "reversals";
  const showShort = lane.key === "latest" || lane.key === "shorts";
  const showAvoid = lane.key === "latest" || lane.key === "avoid";

  return (
    <section
      data-testid={lane.testid}
      className="rounded-lg border border-slate-700/60 bg-slate-900/40 p-4 space-y-3"
    >
      <header className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-bold uppercase tracking-widest text-emerald-200">
            {lane.label}
          </h3>
          <p className="text-xs text-slate-400 mt-1 leading-snug">{lane.description}</p>
        </div>
        <span className="text-[10px] uppercase tracking-widest text-slate-500">
          {state.loading ? "Loading…" : `${rows.length} on watch`}
        </span>
      </header>

      {state.error ? (
        <div className="rounded border border-rose-500/30 bg-rose-500/5 px-3 py-2 text-xs text-rose-200">
          Engine read failed: {state.error}. Surface stays advisory-only.
        </div>
      ) : state.loading ? (
        <div className="text-xs text-slate-500">Loading lane…</div>
      ) : rows.length === 0 ? (
        <div className="rounded border border-dashed border-slate-700 px-3 py-4 text-center text-xs text-slate-500">
          {lane.emptyHint}
        </div>
      ) : (
        <ul className="space-y-2">
          {rows.slice(0, 6).map((c, i) => (
            <CandidateCard
              key={`${c.ticker || "x"}-${i}`}
              c={c}
              showReversal={showReversal}
              showShort={showShort}
              showAvoid={showAvoid}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

export default function FallenEdgePanel() {
  const [latest, setLatest] = useState<LaneState>(EMPTY_LANE);
  const [reversals, setReversals] = useState<LaneState>(EMPTY_LANE);
  const [shorts, setShorts] = useState<LaneState>(EMPTY_LANE);
  const [avoid, setAvoid] = useState<LaneState>(EMPTY_LANE);

  const load = async () => {
    setLatest((s) => ({ ...s, loading: true, error: null }));
    setReversals((s) => ({ ...s, loading: true, error: null }));
    setShorts((s) => ({ ...s, loading: true, error: null }));
    setAvoid((s) => ({ ...s, loading: true, error: null }));
    const [l, r, sh, a] = await Promise.all([
      getFallenEdgeLatest(),
      getFallenEdgeReversals(),
      getFallenEdgeShortContinuations(),
      getFallenEdgeAvoid(),
    ]);
    setLatest({ loading: false, payload: l.ok ? l.data : null, error: l.ok ? null : l.error?.message || "fetch failed" });
    setReversals({ loading: false, payload: r.ok ? r.data : null, error: r.ok ? null : r.error?.message || "fetch failed" });
    setShorts({ loading: false, payload: sh.ok ? sh.data : null, error: sh.ok ? null : sh.error?.message || "fetch failed" });
    setAvoid({ loading: false, payload: a.ok ? a.data : null, error: a.ok ? null : a.error?.message || "fetch failed" });
  };

  useEffect(() => {
    load();
    const id = window.setInterval(load, 120_000);
    return () => window.clearInterval(id);
  }, []);

  // Prefer ET timestamp from engine if present, fall back to formatted UTC.
  const generatedAtET =
    latest.payload?.generated_at_et ||
    formatGeneratedAtET(latest.payload?.generated_at);

  const sampleAvoidScore = latest.payload?.candidates?.[0]?.avoid_score ?? null;
  const aggregateReversal = latest.payload?.candidates?.[0]?.reversal_score ?? null;

  return (
    <div
      data-testid="fallen-edge-panel"
      className="rounded-xl border border-emerald-500/20 bg-slate-950/60 p-4 space-y-4"
    >
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-base md:text-lg font-black uppercase italic tracking-tight text-emerald-200">
            Fallen Edge — Loser Intelligence
          </h2>
          <p className="text-xs text-slate-400 mt-1 max-w-2xl leading-snug">
            Today's losers, scored for reversal, continuation, or avoid. Designed
            to help you decide whether a down-name is worth watching — never to
            tell you to buy or sell.
          </p>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <AdvisoryOnlyBadge />
          <span className="text-[10px] uppercase tracking-widest text-slate-500">
            Updated: {generatedAtET}
          </span>
        </div>
      </header>

      {/* Top-of-panel score summary from the strongest "latest" candidate (when present) */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <ScoreBar
          label="Reversal lean"
          value={aggregateReversal}
          tone="bullish"
          hint="Higher = more setup quality for a bounce."
        />
        <ScoreBar
          label="Short continuation lean"
          value={latest.payload?.candidates?.[0]?.short_continuation_score ?? null}
          tone="bearish"
          hint="Higher = weakness more likely to extend."
        />
        <ScoreBar
          label="Avoid lean"
          value={sampleAvoidScore}
          tone="warning"
          hint="Higher = stand aside, conviction is too low."
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {LANES.map((lane) => {
          const state =
            lane.key === "latest"
              ? latest
              : lane.key === "reversals"
              ? reversals
              : lane.key === "shorts"
              ? shorts
              : avoid;
          return <LaneSection key={lane.key} lane={lane} state={state} />;
        })}
      </div>

      <p className="text-[11px] text-slate-500 leading-snug">
        These lanes are research, not orders. Adversyn never automatically buys
        or sells from this panel; live execution is gated by the deployment,
        risk, liquidity, and account systems.
      </p>
    </div>
  );
}
